// Platform admin page — 2FA gate + 5 tabs + step-up provider.

import { clsx } from "clsx";
import { useState } from "react";
import { Card } from "@/components/ui/Card";
import { PageHeader } from "@/components/ui/PageHeader";
import { SelectMenu } from "@/components/ui/SelectMenu";
import { AdminBlogTab } from "@/components/admin/AdminBlogTab";
import { AdminDashboardTab } from "@/components/admin/AdminDashboardTab";
import { AdminHealthTab } from "@/components/admin/AdminHealthTab";
import { AdminOrgsTab } from "@/components/admin/AdminOrgsTab";
import { AdminSettingsTab } from "@/components/admin/AdminSettingsTab";
import { AdminSystemTab } from "@/components/admin/AdminSystemTab";
import { AdminTokensTab } from "@/components/admin/AdminTokensTab";
import { AdminUsersTab } from "@/components/admin/AdminUsersTab";
import { StepUpProvider, useStepUp } from "@/context/StepUpContext";
import { useUnresolvedCount } from "@/hooks/useAdmin";
import { useAuthStore } from "@/stores/authStore";
import { useDict, fmt } from "@/i18n/LocaleProvider";

type TabId = "dashboard" | "users" | "orgs" | "tokens" | "settings" | "system" | "health" | "blog";

const TAB_META: Array<{ id: TabId; icon: string }> = [
  { id: "dashboard", icon: "▤" },
  { id: "users", icon: "◉" },
  { id: "orgs", icon: "◇" },
  { id: "tokens", icon: "⚿" },
  { id: "settings", icon: "⚙" },
  { id: "system", icon: "⚕" },
  { id: "health", icon: "♥" },
  { id: "blog", icon: "✎" },
];

function AdminPageInner() {
  const t = useDict().app.adminPage;
  const { hasActiveStepUp } = useStepUp();
  const TABS = TAB_META.map((m) => ({ ...m, label: t.tabs[m.id] }));
  const [tab, setTab] = useState<TabId>("dashboard");
  // Polled every 60s (see hook) — the "something is wrong" attention
  // grabber. Clicking it jumps straight to the Health tab.
  const unresolved = useUnresolvedCount().data?.unresolved ?? 0;

  return (
    <div>
      <div className="flex items-center gap-3 mb-[6px]">
        <div
          className="px-[10px] py-1 rounded-[3px] font-mono text-[9px] font-bold uppercase tracking-[0.08em]"
          style={{
            background: "rgb(var(--color-danger) / 0.133)",
            border: "1px solid rgb(var(--color-danger) / 0.533)",
            color: "rgb(var(--color-danger))",
          }}
        >
          {t.platformAdminBadge}
        </div>
        {hasActiveStepUp && (
          <div
            className="px-[10px] py-1 rounded-[3px] font-mono text-[9px] font-bold uppercase"
            style={{
              background: "rgb(var(--color-success) / 0.133)",
              border: "1px solid rgb(var(--color-success) / 0.4)",
              color: "rgb(var(--color-success))",
            }}
          >
            {t.stepUpActive}
          </div>
        )}
        {unresolved > 0 && (
          <button
            type="button"
            onClick={() => setTab("health")}
            className="px-[10px] py-1 rounded-[3px] font-mono text-[9px] font-bold uppercase tracking-[0.08em] cursor-pointer border-none animate-pulse"
            style={{
              background: "rgb(var(--color-danger) / 0.2)",
              border: "1px solid rgb(var(--color-danger))",
              color: "rgb(var(--color-danger))",
            }}
            title={t.openHealth}
          >
            {unresolved === 1 ? fmt(t.unresolvedOne, { n: unresolved }) : fmt(t.unresolvedMany, { n: unresolved })}
          </button>
        )}
      </div>

      <PageHeader
        title={t.title}
        subtitle={t.subtitle}
      />

      <div
        className="px-[14px] py-[10px] rounded text-danger font-mono text-[10px] mb-4"
        style={{
          background: "rgb(var(--color-danger) / 0.067)",
          border: "1px solid rgb(var(--color-danger) / 0.267)",
        }}
      >
        {t.warning}
      </div>

      {/* Mobile: branded SelectMenu (matches Engagement detail + Profile). */}
      <div className="md:hidden mb-4">
        <SelectMenu<TabId>
          value={tab}
          options={TABS.map((t) => ({ value: t.id, label: `${t.icon}  ${t.label}` }))}
          onChange={setTab}
          ariaLabel={t.sectionAria}
        />
      </div>
      {/* Desktop: tab bar */}
      <div className="hidden md:flex gap-[6px] mb-4 border-b border-stroke">
        {TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={clsx(
              "px-4 py-[10px] bg-transparent border-none font-mono text-[12px] font-semibold cursor-pointer uppercase tracking-[0.05em]",
              tab === t.id ? "text-accent" : "text-muted",
            )}
            style={{
              borderBottom:
                tab === t.id ? "2px solid rgb(var(--color-accent))" : "2px solid transparent",
            }}
          >
            {t.icon} {t.label}
          </button>
        ))}
      </div>

      {tab === "dashboard" && <AdminDashboardTab />}
      {tab === "users" && <AdminUsersTab />}
      {tab === "orgs" && <AdminOrgsTab />}
      {tab === "tokens" && <AdminTokensTab />}
      {tab === "settings" && <AdminSettingsTab />}
      {tab === "system" && <AdminSystemTab />}
      {tab === "health" && <AdminHealthTab />}
      {tab === "blog" && <AdminBlogTab />}
    </div>
  );
}

export function AdminPage() {
  const t = useDict().app.adminPage;
  const user = useAuthStore((s) => s.user);

  // Gate: 2FA must be enabled to access the panel at all
  if (!user?.totp_enabled) {
    return (
      <div>
        <PageHeader
          title={t.title}
          subtitle={t.gateSubtitle}
        />
        <Card className="border-danger" style={{ padding: "32px", textAlign: "center" }}>
          <div className="text-5xl mb-3">🔒</div>
          <div className="font-display text-[20px] font-bold text-danger mb-2">
            {t.gateTitle}
          </div>
          <div className="font-mono text-[12px] text-muted leading-[1.6] max-w-[500px] mx-auto mb-4">
            {t.gateBody}
          </div>
        </Card>
      </div>
    );
  }

  return (
    <StepUpProvider>
      <AdminPageInner />
    </StepUpProvider>
  );
}
